import type * as React from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartFrame } from "./chart-frame";
import { describeSeries, describeShares } from "./describe";

export { describeSeries, describeShares } from "./describe";

/** Series colours resolve at paint time from the theme (`--chart-1..5`). */
export const chartColors = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
] as const;

export function chartColor(i: number): string {
  return chartColors[i % chartColors.length] ?? chartColors[0];
}

export interface SeriesSpec {
  key: string;
  label?: string;
  color?: string;
}

interface BaseChartProps {
  /** Chart name: used by the "Read chart" summary and as the accessible label. */
  title?: string;
  data: Record<string, unknown>[];
  height?: number;
  className?: string;
  valueFormatter?: (v: number) => string;
}

const axisStyle = { fontSize: 11, fill: "var(--muted-foreground)" } as const;
const tooltipStyle: React.CSSProperties = {
  background: "var(--popover)",
  color: "var(--popover-foreground)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  fontSize: 12,
};

export interface TimeSeriesProps extends BaseChartProps {
  xKey: string;
  series: SeriesSpec[];
  kind?: "line" | "area";
  xFormatter?: (v: string) => string;
}

export function TimeSeries({
  title,
  data,
  xKey,
  series,
  kind = "line",
  height = 240,
  className,
  valueFormatter,
  xFormatter,
}: TimeSeriesProps) {
  const Chart = kind === "area" ? AreaChart : LineChart;
  return (
    <ChartFrame
      slot="chart-timeseries"
      title={title}
      summary={describeSeries(data, xKey, series, { title, valueFormatter, xFormatter })}
      columns={[
        { key: xKey, label: "" },
        ...series.map((s) => ({ key: s.key, label: s.label ?? s.key, numeric: true })),
      ]}
      rows={data}
      formatCell={(k, v) =>
        k === xKey
          ? xFormatter
            ? xFormatter(String(v ?? ""))
            : String(v ?? "")
          : valueFormatter && typeof v === "number"
            ? valueFormatter(v)
            : String(v ?? "")
      }
      height={height}
      className={className}
    >
      <ResponsiveContainer width="100%" height="100%">
        <Chart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey={xKey}
            tick={axisStyle}
            tickLine={false}
            axisLine={false}
            tickFormatter={xFormatter}
          />
          <YAxis
            tick={axisStyle}
            tickLine={false}
            axisLine={false}
            width={48}
            tickFormatter={valueFormatter}
          />
          <Tooltip
            contentStyle={tooltipStyle}
            formatter={valueFormatter ? (v) => valueFormatter(Number(v)) : undefined}
          />
          {series.length > 1 ? <Legend wrapperStyle={{ fontSize: 12 }} /> : null}
          {series.map((s, i) =>
            kind === "area" ? (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label ?? s.key}
                stroke={s.color ?? chartColor(i)}
                fill={s.color ?? chartColor(i)}
                fillOpacity={0.15}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            ) : (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label ?? s.key}
                stroke={s.color ?? chartColor(i)}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            ),
          )}
        </Chart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

export interface BarsProps extends BaseChartProps {
  xKey: string;
  series: SeriesSpec[];
  stacked?: boolean;
  horizontal?: boolean;
}

export function Bars({
  title,
  data,
  xKey,
  series,
  stacked = false,
  horizontal = false,
  height = 240,
  className,
  valueFormatter,
}: BarsProps) {
  return (
    <ChartFrame
      slot="chart-bars"
      title={title}
      summary={describeSeries(data, xKey, series, { title, valueFormatter })}
      columns={[
        { key: xKey, label: "" },
        ...series.map((s) => ({ key: s.key, label: s.label ?? s.key, numeric: true })),
      ]}
      rows={data}
      formatCell={(k, v) =>
        k !== xKey && valueFormatter && typeof v === "number" ? valueFormatter(v) : String(v ?? "")
      }
      height={height}
      className={className}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout={horizontal ? "vertical" : "horizontal"}
          margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
        >
          <CartesianGrid
            stroke="var(--chart-grid)"
            strokeDasharray="3 3"
            vertical={horizontal}
            horizontal={!horizontal}
          />
          {horizontal ? (
            <>
              <XAxis
                type="number"
                tick={axisStyle}
                tickLine={false}
                axisLine={false}
                tickFormatter={valueFormatter}
              />
              <YAxis
                type="category"
                dataKey={xKey}
                tick={axisStyle}
                tickLine={false}
                axisLine={false}
                width={96}
              />
            </>
          ) : (
            <>
              <XAxis dataKey={xKey} tick={axisStyle} tickLine={false} axisLine={false} />
              <YAxis
                tick={axisStyle}
                tickLine={false}
                axisLine={false}
                width={48}
                tickFormatter={valueFormatter}
              />
            </>
          )}
          <Tooltip
            contentStyle={tooltipStyle}
            cursor={{ fill: "var(--muted)" }}
            formatter={valueFormatter ? (v) => valueFormatter(Number(v)) : undefined}
          />
          {series.length > 1 ? <Legend wrapperStyle={{ fontSize: 12 }} /> : null}
          {series.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label ?? s.key}
              fill={s.color ?? chartColor(i)}
              stackId={stacked ? "a" : undefined}
              radius={stacked ? 0 : 3}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

export interface DonutDatum {
  name: string;
  value: number;
  color?: string;
}

export interface DonutProps {
  title?: string;
  data: DonutDatum[];
  height?: number;
  className?: string;
  valueFormatter?: (v: number) => string;
  /** Text rendered in the centre hole (e.g. a total). */
  center?: React.ReactNode;
}

export function Donut({
  title,
  data,
  height = 220,
  className,
  valueFormatter,
  center,
}: DonutProps) {
  const total = data.reduce((a, d) => a + d.value, 0);
  return (
    <ChartFrame
      slot="chart-donut"
      title={title}
      summary={describeShares(data, { title, valueFormatter })}
      columns={[
        { key: "name", label: "" },
        { key: "value", label: "Value", numeric: true },
        { key: "share", label: "Share", numeric: true },
      ]}
      rows={data.map((d) => ({
        ...d,
        share: total > 0 ? `${Math.round((d.value / total) * 100)}%` : "—",
      }))}
      formatCell={(k, v) =>
        k === "value" && valueFormatter && typeof v === "number"
          ? valueFormatter(v)
          : String(v ?? "")
      }
      height={height}
      className={className}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Tooltip
            contentStyle={tooltipStyle}
            formatter={valueFormatter ? (v) => valueFormatter(Number(v)) : undefined}
          />
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius="62%"
            outerRadius="90%"
            paddingAngle={2}
            stroke="var(--card)"
            isAnimationActive={false}
          >
            {data.map((d, i) => (
              <Cell key={d.name} fill={d.color ?? chartColor(i)} />
            ))}
          </Pie>
          <Legend wrapperStyle={{ fontSize: 12 }} verticalAlign="bottom" />
        </PieChart>
      </ResponsiveContainer>
      {center ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center pb-6 font-bold text-lg tabular">
          {center}
        </div>
      ) : null}
    </ChartFrame>
  );
}
